import { FC } from 'react'

import { PastTargetsFooter } from '../../../modules/appraisal/appraisal-flow/shared/PastTargetsFooter'
import { ObjectiveList, ObjectiveListType } from '../../../modules/appraisal/okr/lists/ObjectivesList'
import { filterObjectivesByTypeStatusAndRefYear } from '../../../modules/appraisal/okr/util'
import {
  LatestContractDtoFragment,
  FullMeetingPreviewDto,
  ObjectiveStatus,
  ObjectiveDto,
  OneOffPayment,
  ScoreGridDto,
  SelfreflectionQuestion,
} from '../../../types'
import BackgroundBox from '../BackgroundBox'
import PerFlexRow from '../FlexRow'
import PerSpacer from '../Spacer'
import { MeetingStatusModalContractChange } from './MeetingStatusModalContractChange'
import { MeetingStatusModalDivider } from './MeetingStatusModalDivider'
import { MeetingStatusModalEvaluation } from './MeetingStatusModalEvaluation'
import { MeetingStatusModalFeedback } from './MeetingStatusModalFeedback'
import { MeetingModalStatusHeader } from './MeetingStatusModalHeader'
import { MeetingStatusModalPayment } from './MeetingStatusModalPayment'
import { MeetingStatusModalPaymentFooter } from './MeetingStatusModalPaymentFooter'
import { MeetingStatusModalPreparation } from './MeetingStatusModalPreparation'
import { MeetingStatusModalSelfReflection } from './MeetingStatusModalSelfReflection'
import { retrieveContractStartAndEndDateFromContract } from '../../../modules/profile/contract/utils/contractUtil'

interface MeetingStatusModalContainerProps {
  fullMeetingPreview: FullMeetingPreviewDto
  isAppraisalUpdate: boolean
  employeeTimezone: string
  isBonusValidationEnabled: boolean
  userIsHR: boolean
}

const hasFeedback = (fullMeetingPreview: FullMeetingPreviewDto) => {
  const appraisal = fullMeetingPreview.meeting.appraisal
  return (
    !!appraisal &&
    ((fullMeetingPreview.meeting.employeePrepared && !!appraisal.employeeGeneralFeedback) || !!appraisal.reviewerGeneralFeedback)
  )
}

const hasSelfReflection = (fullMeetingPreview: FullMeetingPreviewDto) => {
  return !!fullMeetingPreview.selfReflectionQuestions && !!fullMeetingPreview.selfReflectionQuestions.length
}

const hasScoreGrid = (fullMeetingPreview: FullMeetingPreviewDto) => {
  return !!fullMeetingPreview.scoreGrid
}

const hasTargets = (targets: ObjectiveDto[]) => {
  return !!targets.length
}

const hasPayments = (objectivesToBePaid: ObjectiveDto[], oneOffPayment: OneOffPayment | null) => {
  return (!!objectivesToBePaid && !!objectivesToBePaid.length) || !!oneOffPayment
}

const hasContractChanges = (fullMeetingPreview: FullMeetingPreviewDto) => {
  return !!fullMeetingPreview.contractChanges && !!fullMeetingPreview.contractChanges.length
}

export const MeetingStatusModalContainer: FC<MeetingStatusModalContainerProps> = ({
  fullMeetingPreview,
  isAppraisalUpdate,
  employeeTimezone,
  isBonusValidationEnabled,
  userIsHR,
}) => {
  const {
    targetsCalculationHelper: { pastTargetsReferenceYear, newTargetsReferenceYear },
  } = fullMeetingPreview.meeting

  const pastMonetaryTargets = filterObjectivesByTypeStatusAndRefYear(
    fullMeetingPreview.objectives,
    true,
    [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved, ObjectiveStatus.DismissedPending],
    pastTargetsReferenceYear,
  )

  const pastDevelopmentTargets = filterObjectivesByTypeStatusAndRefYear(
    fullMeetingPreview.objectives,
    false,
    [ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone, ObjectiveStatus.PastApproved, ObjectiveStatus.DismissedPending],
    pastTargetsReferenceYear,
  )

  const objectivesToBePaid = filterObjectivesByTypeStatusAndRefYear(fullMeetingPreview.objectives, true, [
    ObjectiveStatus.Approved,
    ObjectiveStatus.PastApproved,
  ]).filter((x) => x.achievedAmount !== null && x.achievedAmount !== undefined)

  const newMonetaryTargets = filterObjectivesByTypeStatusAndRefYear(
    fullMeetingPreview.objectives,
    true,
    [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager],
    newTargetsReferenceYear,
  )

  const newDevelopmentTargets = filterObjectivesByTypeStatusAndRefYear(
    fullMeetingPreview.objectives,
    false,
    [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager],
    newTargetsReferenceYear,
  )

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(fullMeetingPreview.latestContract)

  return (
    <>
      <PerFlexRow mt="l">
        <MeetingModalStatusHeader heading="appraisalStatusModal.statusOverview" />
      </PerFlexRow>
      <div data-testid="MeetingStatusModalPreparation">
        <PerFlexRow mt="xxl">
          <MeetingStatusModalPreparation meeting={fullMeetingPreview.meeting} />
        </PerFlexRow>
      </div>
      {hasFeedback(fullMeetingPreview) && (
        <div data-testid="MeetingStatusModalFeedback">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.overview" />
          </PerFlexRow>
          <PerFlexRow mt="xxl">
            <MeetingStatusModalFeedback meeting={fullMeetingPreview.meeting} />
          </PerFlexRow>
        </div>
      )}
      {hasSelfReflection(fullMeetingPreview) && (
        <div data-testid="MeetingStatusModalSelfRefelction">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.selfReflection" />
          </PerFlexRow>
          <PerFlexRow mt="xxl">
            <MeetingStatusModalSelfReflection
              meeting={fullMeetingPreview.meeting}
              selfReflectionQuestions={fullMeetingPreview.selfReflectionQuestions as SelfreflectionQuestion[]}
              userIsHR={userIsHR}
            />
          </PerFlexRow>
        </div>
      )}
      {hasScoreGrid(fullMeetingPreview) && (
        <div data-testid="MeetingStatusModalScoreGrid">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.evaluation" />
          </PerFlexRow>
          <PerFlexRow mt="xxl">
            <MeetingStatusModalEvaluation
              scoreGrid={fullMeetingPreview.scoreGrid as ScoreGridDto}
              meeting={fullMeetingPreview.meeting}
              userIsHR={userIsHR}
            />
          </PerFlexRow>
        </div>
      )}
      {!isAppraisalUpdate && (hasTargets(pastMonetaryTargets) || hasTargets(pastDevelopmentTargets)) && (
        <div data-testid="MeetingStatusModalPastTargets">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.pastTargets" />
          </PerFlexRow>
          {hasTargets(pastMonetaryTargets) && (
            <div data-testid="MeetingStatusModalPastTargets-monetary">
              <PerFlexRow mt="xxl">
                <ObjectiveList
                  listType={ObjectiveListType.PastMonetary}
                  objectives={pastMonetaryTargets}
                  handleObjectiveClicked={() => null}
                  showStatus
                  showToBePaidAndBonus
                  hideAction
                  fullWidth
                />
              </PerFlexRow>
            </div>
          )}
          {hasTargets(pastDevelopmentTargets) && (
            <div data-testid="MeetingStatusModalPastTargets-development">
              <PerFlexRow mt={hasTargets(pastMonetaryTargets) ? 'xs' : 'xxl'}>
                <ObjectiveList
                  listType={ObjectiveListType.PastDevelopment}
                  objectives={pastDevelopmentTargets}
                  handleObjectiveClicked={() => null}
                  showStatus
                  showToBePaidAndBonus
                  hideAction
                  fullWidth
                />
              </PerFlexRow>
            </div>
          )}
          <PerFlexRow mt="s">
            <BackgroundBox r noShadow color="gray-2" className="appraisal-footer fullWidth">
              <PastTargetsFooter
                objectives={fullMeetingPreview.objectives}
                targetBonusOfYear={fullMeetingPreview.targetBonusOfYear}
                salaryOfYear={fullMeetingPreview.salaryOfYear}
                currentSalary={fullMeetingPreview.latestContract.annualSalary ? fullMeetingPreview.latestContract.annualSalary.amount : 0}
                currentBonus={fullMeetingPreview.latestContract.targetBonus ? fullMeetingPreview.latestContract.targetBonus.amount : 0}
                referenceYear={pastTargetsReferenceYear}
                contractStartDate={contractStartDate}
                contractEndDate={contractEndDate}
                employeeTimezone={employeeTimezone}
                isBonusValidationEnabled={isBonusValidationEnabled}
                contractChangesByEmployee={fullMeetingPreview.contractChanges}
              />
            </BackgroundBox>
          </PerFlexRow>
        </div>
      )}
      {hasPayments(objectivesToBePaid, fullMeetingPreview.oneOffPayment || null) && (
        <div data-testid="MeetingStatusModalPayment">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.payment" />
          </PerFlexRow>
          <PerFlexRow mt="xxl">
            <MeetingStatusModalPayment
              objectives={objectivesToBePaid}
              objectivePayment={fullMeetingPreview.objectivePayment || undefined}
              oneOffPayment={fullMeetingPreview.oneOffPayment || undefined}
              targetCorrectionPayment={fullMeetingPreview.targetCorrectionPayment || undefined}
            />
          </PerFlexRow>
          <PerFlexRow mt="s">
            <MeetingStatusModalPaymentFooter
              objectives={objectivesToBePaid}
              objectivePayment={fullMeetingPreview.objectivePayment || undefined}
              oneOffPayment={fullMeetingPreview.oneOffPayment || undefined}
              targetCorrectionPayment={fullMeetingPreview.targetCorrectionPayment || undefined}
            />
          </PerFlexRow>
        </div>
      )}
      {hasContractChanges(fullMeetingPreview) && (
        <div data-testid="MeetingStatusModalContractChanges">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.contractChanges" />
          </PerFlexRow>
          <PerFlexRow mt="xxl">
            <MeetingStatusModalContractChange
              contractChange={fullMeetingPreview.contractChanges}
              latestContract={fullMeetingPreview.latestContract as LatestContractDtoFragment}
            />
          </PerFlexRow>
        </div>
      )}
      {(hasTargets(newMonetaryTargets) || hasTargets(newDevelopmentTargets)) && (
        <div data-testid="MeetingStatusModalNewTargets">
          <PerFlexRow mt="xxxl">
            <MeetingStatusModalDivider />
          </PerFlexRow>
          <PerFlexRow mt="m">
            <MeetingModalStatusHeader heading="appraisalStatusModal.newTargets" />
          </PerFlexRow>
          {hasTargets(newMonetaryTargets) && (
            <div data-testid="MeetingStatusModalNewTargets-monetary">
              <PerFlexRow mt="xxl">
                <ObjectiveList
                  listType={ObjectiveListType.Monetary}
                  objectives={newMonetaryTargets}
                  handleObjectiveClicked={() => null}
                  showStartDate
                  showTasks
                  hideAction
                  fullWidth
                />
              </PerFlexRow>
            </div>
          )}
          {hasTargets(newDevelopmentTargets) && (
            <div data-testid="MeetingStatusModalNewTargets-development">
              <PerFlexRow mt={hasTargets(newMonetaryTargets) ? 'xs' : 'xxl'}>
                <ObjectiveList
                  listType={ObjectiveListType.Development}
                  objectives={newDevelopmentTargets}
                  handleObjectiveClicked={() => null}
                  showStartDate
                  showTasks
                  hideAction
                  fullWidth
                />
              </PerFlexRow>
            </div>
          )}
        </div>
      )}
      <PerSpacer mt="xxl" />
    </>
  )
}
